import { useIntl } from 'react-intl';
import { useResource } from 'react-resource-router';

import Button from '@atlaskit/button/new';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';

import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { useAnalytics } from 'src/components/settings/analytics';
import { useParams } from 'src/router/hooks';

import { useCreateEditMergeQueue } from '../hooks/use-create-edit-merge-queue';
import { messages } from '../merge-queues.i18n';
import { mergeQueuesRouteResource } from '../resources';
import { MergeQueueState } from '../types';

export const MergeQueueActivateModal = ({
  onClose,
  focusedMergeQueue,
}: {
  onClose: () => void;
  focusedMergeQueue: MergeQueue | undefined;
}) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: 'mergeQueueActivateModal',
  });
  const { formatMessage } = useIntl();
  const { repositoryOwner: owner, repositorySlug: slug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();
  const { refresh: refreshMergeQueues } = useResource(mergeQueuesRouteResource);

  const { editMergeQueue } = useCreateEditMergeQueue({
    owner,
    slug,
    onClose,
    onSuccess: refreshMergeQueues,
  });

  return (
    <Modal onClose={onClose} width="small">
      <ModalHeader>
        <ModalTitle appearance="warning">
          {formatMessage(messages.mergeQueueActivateModalHeader)}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        {formatMessage(messages.mergeQueueActivateModalBody)}
      </ModalBody>
      <ModalFooter>
        <Button appearance="subtle" onClick={onClose}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          appearance="primary"
          onClick={() => {
            publishButtonClickEvent('confirmActivate');
            if (focusedMergeQueue) {
              editMergeQueue({
                state: MergeQueueState.ACTIVE,
                mergeQueueUuid: focusedMergeQueue.uuid,
              });
            }
          }}
        >
          {formatMessage(messages.activate)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
